/**
 * Token refresh helper — exchange refresh token for a new access token.
 * Used by the API client fetch wrapper and the upload helpers so refresh
 * logic lives in one place.
 *
 * @module lib/auth-refresh
 */

import { storage } from '@api/lib/storage';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Calls the backend `/auth/refresh` endpoint to exchange a refresh token for
 * a new access token. On success the new access token is persisted to local
 * storage. On failure both tokens are cleared so the user is forced to
 * re-authenticate.
 */
export async function refreshAccessToken(baseUrl: string, refreshToken: string): Promise<string | null> {
	try {
		const response = await fetch(`${baseUrl}/api/auth/refresh`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ refreshToken }),
		});

		if (!response.ok) throw new Error('Refresh request failed');

		const data = await response.json();
		if (data.tokens?.accessToken) {
			await storage.local.set('accessToken', data.tokens.accessToken);
			if (data.tokens.refreshToken) {
				await storage.local.set('refreshToken', data.tokens.refreshToken);
			}
			if (data.tokens?.sessionId) {
				await storage.local.set('currentSessionId', data.tokens.sessionId);
			}
			return data.tokens.accessToken;
		}
	} catch (e) {
		console.error('Refresh failed', e);
	}

	await storage.local.delete('accessToken');
	await storage.local.delete('refreshToken');
	await storage.local.delete('currentSessionId');
	return null;
}
